import { useRouterState } from "@tanstack/react-router";
import { Link } from "./link";
import { useEffect, useState, type ReactNode } from "react";

import { CONTACT_EMAIL, LANGS, getDict, href, type Dictionary, type Lang } from "@/content/site";

import { SealButton, WhatsAppLink } from "./ctas";
import { SmoothScroll } from "./motion";

const LANG_PREFIX = new RegExp(`^/(${LANGS.join("|")})(?=/|$)`);

export function Wordmark({ lang, large }: { lang: Lang; large?: boolean }) {
  return (
    <Link className={`im-wordmark${large ? " im-wordmark--lg" : ""}`} to={href(lang)}>
      {/* Accessible name comes from the text itself ("ins" + sr-only "i" + "dermark"). */}
      <span>ins</span>
      <i aria-hidden="true">ı</i>
      <span className="sr-only">i</span>
      <span>dermark</span>
    </Link>
  );
}

function LangSwitch({ lang, path }: { lang: Lang; path: string }) {
  return (
    <nav className="im-lang" aria-label={getDict(lang).a11y.language}>
      {LANGS.map((l) => (
        <a key={l} href={`/${l}${path}`} aria-current={l === lang ? "true" : undefined} hrefLang={l}>
          {l.toUpperCase()}
        </a>
      ))}
    </nav>
  );
}

export function SiteNav({ lang, t }: { lang: Lang; t: Dictionary }) {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const rest = pathname.replace(LANG_PREFIX, "") || "";

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setSolid(window.scrollY > 24);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const links = [
    { label: t.nav.divisions, to: href(lang, "/divisions") },
    { label: t.nav.advantage, to: href(lang, "/beyond-sourcing") },
    { label: t.nav.how, to: href(lang, "/how-we-work") },
    { label: t.nav.markets, to: href(lang, "/markets") },
    { label: t.nav.network, to: href(lang, "/manufacturing-network") },
    { label: t.nav.about, to: href(lang, "/about") },
  ];

  return (
    <>
      <header className="im-nav" data-solid={solid || open ? "true" : "false"}>
        <div className="im-wrap im-nav__inner">
          <Wordmark lang={lang} />
          <nav className="im-nav__links" aria-label={t.a11y.primary}>
            {links.map((l) => (
              <Link
                key={l.to}
                className="im-nav__link"
                to={l.to}
                aria-current={pathname.startsWith(l.to) ? "page" : undefined}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="im-nav__right">
            <LangSwitch lang={lang} path={rest} />
            <span className="hidden md:inline-flex">
              <SealButton to={href(lang, "/rfq")} small>
                {t.nav.rfq}
              </SealButton>
            </span>
            <button
              className="im-burger"
              type="button"
              aria-expanded={open}
              aria-label={open ? t.nav.close : t.nav.menu}
              onClick={() => setOpen((v) => !v)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>
      <div className="im-drawer" data-open={open ? "true" : "false"} aria-hidden={!open}>
        {links.map((l) => (
          <Link key={l.to} to={l.to}>
            {l.label}
          </Link>
        ))}
        <Link to={href(lang, "/contact")}>{t.nav.contact}</Link>
        <div className="mt-6">
          <SealButton to={href(lang, "/rfq")}>{t.nav.rfq}</SealButton>
        </div>
      </div>
    </>
  );
}

export function SiteFooter({ lang, t }: { lang: Lang; t: Dictionary }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const rest = pathname.replace(LANG_PREFIX, "") || "";
  return (
    <footer className="im-footer" data-surface="dark">
      <div className="im-wrap">
        <div className="im-footer__grid">
          <div className="im-footer__col">
            <Wordmark lang={lang} large />
            <p className="im-mono mt-5">{t.footer.address}</p>
            <p className="im-mono mt-1">
              <a href={`mailto:${CONTACT_EMAIL}`} className="im-muted">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="im-muted mt-6 max-w-[34ch] text-sm leading-relaxed">{t.footer.legal}</p>
          </div>
          {t.footer.columns.map((col) => (
            <div className="im-footer__col" key={col.title}>
              <h3>{col.title}</h3>
              {col.links.map((l) => (
                <Link key={l.to} to={href(lang, l.to)}>
                  {l.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
        <div className="im-footer__bottom">
          <span className="im-mono">{t.languagesNote}</span>
          <span className="im-mono im-footer__legal">
            <Link to={href(lang, "/privacy")}>{t.footer.privacy}</Link>
            <Link to={href(lang, "/terms")}>{t.footer.terms}</Link>
          </span>
          <LangSwitch lang={lang} path={rest} />
        </div>
      </div>
    </footer>
  );
}

export function SiteShell({ lang, t, children }: { lang: Lang; t: Dictionary; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname.replace(/\/$/, "") === `/${lang}`;
  return (
    <div className="im-body" lang={lang} data-surface={isHome ? "dark" : "light"}>
      <SmoothScroll />
      <SiteNav lang={lang} t={t} />
      {children}
      <SiteFooter lang={lang} t={t} />
      <WhatsAppLink label={t.nav.whatsapp} fixed />
    </div>
  );
}
